import { ReactElement, useEffect, useMemo, useState } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'
import useLatest from 'react-use/lib/useLatest'
import { find } from 'lodash-es'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { LoadingButton } from '@mui/lab'
import {
  AccordionSummary,
  AccordionDetails,
  Typography,
  Box,
  IconButton,
  Tooltip,
} from '@mui/material'
import { MaterialSymbol } from '@ui2/icons'
import { SaveButton } from '@ui/actions'
import { FormAccordion } from '@ui/forms'
import { useSnackbar } from '@ui/snackbar'
import { useTransmissionStore } from '@editorStores'
import { useBlockAction, useHideTensileTransmitters, useSceneElement } from '@editorHooks'
import { useStructuralPlanningSelection } from '../../../hooks'
import TensileTransmitterForm from '../TensileTransmitterForm'
import {
  TENSILE_TRANSMISSION_FORM_BLOCK_ACTION,
  TENSILE_TRANSMISSION_PANE_BLOCK,
} from '../constants'
import { TensileTransmitterFormValues } from '../types'
import {
  getElementSupports,
  getSupportsWithTargets,
  getTensilePreviewGraph,
  isTensileFormValuesEqual,
} from '../utils/graph'
import LocalTensileTransmittersPreview from './LocalTensileTransmittersPreview'

interface Props {
  selectedElement: string
  /** The persisted state of the form values, what "Rückgängig" goes back to */
  savedValues: TensileTransmitterFormValues
  onRecalculate: () => void
  isLoading: boolean
  isRecalculating: boolean
}

const TensileTransmitterListFields = ({
  selectedElement,
  savedValues,
  onRecalculate,
  isLoading,
  isRecalculating,
}: Props): ReactElement => {
  const {
    params: { connectorGuid },
    actions: { setConnector },
  } = useStructuralPlanningSelection()
  const { enqueueSnackbar } = useSnackbar()
  const { reset } = useFormContext<TensileTransmitterFormValues>()
  const savedGraph = useTransmissionStore(state => state.tensileTransmissionGraph)
  const values = useWatch<TensileTransmitterFormValues>() as TensileTransmitterFormValues

  // The form edits the targets of the element's supports, the supports themselves are fixed
  const elementSupports = useMemo(
    () => getElementSupports(savedGraph, selectedElement),
    [savedGraph, selectedElement],
  )

  const supports = useMemo(
    () => getSupportsWithTargets(elementSupports, values.targets),
    [elementSupports, values.targets],
  )

  const [openTargetGuid, setOpenTargetGuid] = useState<string | null>(null)

  // The open target of the open transmitter: the one opened last, else its first target
  const activeTargetGuid = useMemo(() => {
    const targets = find(supports, ['guid', connectorGuid])?.targets ?? []
    return find(targets, ['guid', openTargetGuid])?.guid ?? targets[0]?.guid
  }, [supports, connectorGuid, openTargetGuid])

  const targetIndexByGuid = useMemo(
    () => new Map(values.targets.map(({ guid }, index) => [guid, index])),
    [values.targets],
  )

  const isDirty = useMemo(
    () => !isTensileFormValuesEqual(values, savedValues),
    [values, savedValues],
  )

  // While unsaved, refuse leaving this pane (other element, tab, mode, ESC, storey/2D-3D switch)
  useBlockAction(
    TENSILE_TRANSMISSION_FORM_BLOCK_ACTION,
    isDirty ? 'provider' : undefined,
    TENSILE_TRANSMISSION_PANE_BLOCK,
  )

  // The scene shows the form's graph for this element instead of the saved one: unsaved edits
  // never reach the store
  useHideTensileTransmitters(selectedElement)

  const previewGraph = useMemo(
    () => getTensilePreviewGraph(elementSupports, values.targets),
    [elementSupports, values.targets],
  )

  const previewProps = useMemo(
    () => ({
      graph: previewGraph,
      selectedElement,
      connectorGuid,
      onClick: setConnector,
    }),
    [previewGraph, selectedElement, connectorGuid, setConnector],
  )

  useSceneElement({
    enabled: true,
    properties: previewProps,
    component: LocalTensileTransmittersPreview,
  })

  // Open the first transmitter when the pane opens (or the element gets its first one). Not when
  // the connector is cleared later, so ESC can still drop it and then leave the element
  const latestSelection = useLatest({ connectorGuid, supports, setConnector })
  const hasSupports = supports.length > 0

  useEffect(() => {
    const { connectorGuid, supports, setConnector } = latestSelection.current
    if (!connectorGuid && supports.length) {
      const { guid, element_guid } = supports[0]
      setConnector(guid, element_guid)
    }
  }, [hasSupports, latestSelection])

  // Recalculating replaces the element's targets with the server's, so unsaved edits would be lost
  const handleRecalculate = () => {
    if (isDirty) {
      enqueueSnackbar(
        TENSILE_TRANSMISSION_PANE_BLOCK.message,
        TENSILE_TRANSMISSION_PANE_BLOCK.options,
      )
      return
    }

    onRecalculate()
  }

  return (
    <>
      <Box>
        {supports.map((support, index) => {
          const { guid, element_guid, targets } = support

          return (
            <FormAccordion
              fields={targets.flatMap(target => {
                const fieldName = `targets.${targetIndexByGuid.get(target.guid)}`
                return [`${fieldName}.transmission_factor`, `${fieldName}.relative_position`]
              })}
              key={guid}
              expanded={guid === connectorGuid}
              onChange={(_, expanded) => {
                if (expanded) setConnector(guid, element_guid)
              }}
              data-cy={`tensile-support-form-${index}`}
            >
              <AccordionSummary
                key={guid}
                expandIcon={<ExpandMoreIcon />}
                aria-controls="panel1a-content"
                id={support.guid}
              >
                <Typography data-cy="tensile-support-title" variant="body2">
                  Transmitter {index + 1}
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                {guid === connectorGuid && (
                  <TensileTransmitterForm
                    support={support}
                    targetIndexByGuid={targetIndexByGuid}
                    activeTargetGuid={activeTargetGuid}
                    onActiveTargetChange={setOpenTargetGuid}
                  />
                )}
              </AccordionDetails>
            </FormAccordion>
          )
        })}
      </Box>
      {!!supports.length && (
        <Box display="flex" justifyContent="end" marginY={2}>
          <LoadingButton
            sx={{ marginRight: 1 }}
            onClick={handleRecalculate}
            variant="outlined"
            color="primary"
            size="small"
            loading={isRecalculating}
            data-cy="btn-recalculate-tensile-targets"
          >
            Ziele neu berechnen
          </LoadingButton>
          <SaveButton data-cy="btn-save" type="submit" size="small" loading={isLoading}>
            Speichern
          </SaveButton>
          <Tooltip title="Rückgängig" enterDelay={1000} enterNextDelay={1000}>
            {/* A disabled button fires no hover events — the span keeps the tooltip working. */}
            <span>
              <IconButton
                size="small"
                onClick={() => reset(savedValues)}
                disabled={!isDirty || isLoading}
                aria-label="Rückgängig"
                data-cy="btn-discard-tensile-changes"
              >
                <MaterialSymbol icon="undo" size={18} />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      )}
    </>
  )
}

export default TensileTransmitterListFields
